import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  MessageCircle,
  QrCode,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Power,
  RefreshCw,
  Send,
  Smartphone,
  Server,
  Activity,
  Cloud,
  Copy,
  Plus,
  FileText,
  ExternalLink,
  ShieldCheck,
} from 'lucide-react';
import {
  apiClient,
  whatsappCloudClient,
  type WhatsAppInstance,
  type WhatsAppTemplate,
} from '../lib/api.js';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card.js';
import { Badge } from '../components/ui/Badge.js';
import { Button } from '../components/ui/Button.js';
import { cn, formatRelative } from '../lib/utils.js';

const statusConfig: Record<
  WhatsAppInstance['status'],
  { label: string; color: string; icon: typeof CheckCircle2 }
> = {
  connected: {
    label: 'Conectado',
    color: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    icon: CheckCircle2,
  },
  qr: {
    label: 'Aguardando QR',
    color: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
    icon: QrCode,
  },
  connecting: {
    label: 'Conectando…',
    color: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
    icon: Loader2,
  },
  disconnected: {
    label: 'Desconectado',
    color: 'bg-slate-500/15 text-slate-400 border-slate-500/30',
    icon: Power,
  },
  error: {
    label: 'Erro',
    color: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
    icon: AlertCircle,
  },
};

function copyToClipboard(text: string): void {
  void navigator.clipboard.writeText(text);
}

// ============================================================
// Baileys (Evolution) — card original
// ============================================================
function BaileysCard({ instance }: { instance: WhatsAppInstance }): JSX.Element {
  const qc = useQueryClient();
  const [polling, setPolling] = useState(false);
  const [testNumber, setTestNumber] = useState('');
  const [testText, setTestText] = useState('Teste NextBoot CRM');
  const [testResult, setTestResult] = useState<string | null>(null);

  const qrQuery = useQuery({
    queryKey: ['wa-qr', instance.id],
    queryFn: () => apiClient.whatsappQr(instance.id),
    refetchInterval: polling ? 3000 : false,
    enabled: polling,
  });

  useEffect(() => {
    if (qrQuery.data?.status === 'connected') {
      setPolling(false);
      qc.invalidateQueries({ queryKey: ['wa-instances'] });
    }
  }, [qrQuery.data?.status, qc]);

  const connectMut = useMutation({
    mutationFn: () => apiClient.whatsappConnect(instance.id),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['wa-instances'] });
      if (data.status !== 'connected') setPolling(true);
    },
  });

  const disconnectMut = useMutation({
    mutationFn: () => apiClient.whatsappDisconnect(instance.id),
    onSuccess: () => {
      setPolling(false);
      qc.invalidateQueries({ queryKey: ['wa-instances'] });
    },
  });

  const sendTestMut = useMutation({
    mutationFn: () => apiClient.whatsappSendTest(instance.id, testNumber, testText),
    onSuccess: () => setTestResult('✓ Enviado'),
    onError: (e) => setTestResult(`✗ ${(e as Error).message}`),
  });

  const qrBase64 = qrQuery.data?.qrBase64 ?? null;
  const currentStatus = qrQuery.data?.status ?? instance.status;
  const phoneNumber = qrQuery.data?.phoneNumber ?? instance.phoneNumber;
  const cfg = statusConfig[currentStatus as WhatsAppInstance['status']] ?? statusConfig.disconnected;
  const Icon = cfg.icon;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-emerald-500/10 p-2">
              <MessageCircle className="h-5 w-5 text-emerald-400" />
            </div>
            <div>
              <div className="text-base font-semibold flex items-center gap-2">
                {instance.name}
                <Badge variant="outline" className="text-[10px] bg-slate-500/15 text-slate-300 border-slate-500/30">
                  Baileys
                </Badge>
              </div>
              <div className="text-xs text-muted-foreground capitalize">
                {instance.purpose} · {instance.instanceName}
              </div>
            </div>
          </div>
          <Badge variant="outline" className={cn('text-xs flex items-center gap-1', cfg.color)}>
            <Icon className={cn('h-3.5 w-3.5', currentStatus === 'connecting' && 'animate-spin')} />
            {cfg.label}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {currentStatus === 'connected' && (
          <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-4 space-y-2">
            <div className="flex items-center gap-2 text-emerald-300 text-sm font-medium">
              <CheckCircle2 className="h-4 w-4" />
              WhatsApp conectado
            </div>
            {phoneNumber && (
              <div className="text-sm flex items-center gap-2">
                <Smartphone className="h-4 w-4 text-muted-foreground" />
                {phoneNumber}
              </div>
            )}
            {instance.lastConnectedAt && (
              <div className="text-xs text-muted-foreground">
                Online {formatRelative(instance.lastConnectedAt)}
              </div>
            )}
          </div>
        )}

        {(currentStatus === 'qr' || polling) && qrBase64 && (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4">
            <div className="flex items-center gap-2 text-amber-300 text-sm font-medium mb-3">
              <QrCode className="h-4 w-4" />
              Escaneie no WhatsApp do celular
            </div>
            <div className="flex justify-center">
              <img
                src={qrBase64}
                alt="QR Code WhatsApp"
                className="bg-white p-2 rounded-md w-64 h-64 object-contain"
              />
            </div>
            <div className="mt-3 text-xs text-muted-foreground text-center">
              <p>WhatsApp → Configurações → Aparelhos conectados → Conectar aparelho</p>
            </div>
          </div>
        )}

        {instance.status === 'error' && instance.lastError && (
          <div className="rounded-lg border border-rose-500/30 bg-rose-500/5 p-3 text-sm text-rose-300">
            <div className="font-medium mb-1 flex items-center gap-1.5">
              <AlertCircle className="h-4 w-4" />
              Falha
            </div>
            <div className="text-xs">{instance.lastError}</div>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {currentStatus !== 'connected' && (
            <Button
              onClick={() => connectMut.mutate()}
              disabled={connectMut.isPending}
              className="flex items-center gap-2"
            >
              {connectMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <QrCode className="h-4 w-4" />}
              {polling ? 'Gerar novo QR' : 'Conectar / Mostrar QR'}
            </Button>
          )}
          {currentStatus === 'connected' && (
            <Button
              variant="outline"
              onClick={() => disconnectMut.mutate()}
              disabled={disconnectMut.isPending}
              className="flex items-center gap-2"
            >
              <Power className="h-4 w-4" />
              Desconectar
            </Button>
          )}
        </div>

        {currentStatus === 'connected' && (
          <div className="border-t border-border pt-4 space-y-2">
            <div className="text-xs font-medium text-muted-foreground">Teste rápido de envio</div>
            <div className="flex flex-wrap gap-2">
              <input
                type="tel"
                placeholder="+5511999999999"
                value={testNumber}
                onChange={(e) => setTestNumber(e.target.value)}
                className="flex-1 min-w-[180px] rounded-md bg-muted border border-border px-3 py-1.5 text-sm"
              />
              <input
                type="text"
                placeholder="Texto"
                value={testText}
                onChange={(e) => setTestText(e.target.value)}
                className="flex-[2] min-w-[200px] rounded-md bg-muted border border-border px-3 py-1.5 text-sm"
              />
              <Button
                size="sm"
                onClick={() => sendTestMut.mutate()}
                disabled={!testNumber || !testText || sendTestMut.isPending}
                className="flex items-center gap-1.5"
              >
                {sendTestMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Enviar
              </Button>
            </div>
            {testResult && (
              <div className={cn('text-xs', testResult.startsWith('✓') ? 'text-emerald-400' : 'text-rose-400')}>
                {testResult}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================
// Cloud API — card com setup-wizard
// ============================================================
function CloudCard({ instance }: { instance: WhatsAppInstance }): JSX.Element {
  const qc = useQueryClient();
  const [showCredsForm, setShowCredsForm] = useState(false);
  const [creds, setCreds] = useState({
    phone_number_id: instance.phoneNumberId ?? '',
    business_account_id: instance.businessAccountId ?? '',
    access_token: '',
    app_secret: '',
    verify_token: '',
  });
  const [testTo, setTestTo] = useState('');
  const [testMode, setTestMode] = useState<'text' | 'template'>('template');
  const [testText, setTestText] = useState('');
  const [testTemplate, setTestTemplate] = useState('');
  const [testResult, setTestResult] = useState<string | null>(null);

  const setupInfoQuery = useQuery({
    queryKey: ['wa-cloud-setup', instance.id],
    queryFn: () => whatsappCloudClient.setupInfo(instance.id),
  });

  const templatesQuery = useQuery({
    queryKey: ['wa-cloud-templates', instance.id],
    queryFn: () => whatsappCloudClient.templates(instance.id),
    enabled: instance.status === 'connected',
  });

  const saveCredsMut = useMutation({
    mutationFn: () => whatsappCloudClient.saveCredentials(instance.id, creds),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wa-instances'] });
      qc.invalidateQueries({ queryKey: ['wa-cloud-setup', instance.id] });
      setShowCredsForm(false);
    },
  });

  const recheckMut = useMutation({
    mutationFn: () => whatsappCloudClient.health(instance.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['wa-instances'] }),
  });

  const syncTemplatesMut = useMutation({
    mutationFn: () => whatsappCloudClient.syncTemplates(instance.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['wa-cloud-templates', instance.id] }),
  });

  const testSendMut = useMutation({
    mutationFn: () =>
      whatsappCloudClient.testSend(instance.id, {
        to: testTo,
        mode: testMode,
        text: testMode === 'text' ? testText : undefined,
        template_name: testMode === 'template' ? testTemplate : undefined,
        template_language: 'pt_BR',
      }),
    onSuccess: () => setTestResult('✓ Enviado'),
    onError: (e) => setTestResult(`✗ ${(e as Error).message}`),
  });

  const cfg = statusConfig[instance.status];
  const Icon = cfg.icon;
  const setupInfo = setupInfoQuery.data;
  const templates = templatesQuery.data?.templates ?? [];
  const approvedTemplates = templates.filter((t) => t.status === 'APPROVED');

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-blue-500/10 p-2">
              <Cloud className="h-5 w-5 text-blue-400" />
            </div>
            <div>
              <div className="text-base font-semibold flex items-center gap-2">
                {instance.name}
                <Badge variant="outline" className="text-[10px] bg-blue-500/15 text-blue-300 border-blue-500/30">
                  Cloud API
                </Badge>
              </div>
              <div className="text-xs text-muted-foreground capitalize">
                {instance.purpose} · {instance.displayPhoneNumber ?? instance.phoneNumberId ?? 'sem credenciais'}
              </div>
            </div>
          </div>
          <Badge variant="outline" className={cn('text-xs flex items-center gap-1', cfg.color)}>
            <Icon className={cn('h-3.5 w-3.5', instance.status === 'connecting' && 'animate-spin')} />
            {cfg.label}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Conectado */}
        {instance.status === 'connected' && instance.displayPhoneNumber && (
          <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-4 space-y-1">
            <div className="flex items-center gap-2 text-emerald-300 text-sm font-medium">
              <CheckCircle2 className="h-4 w-4" />
              WhatsApp Cloud conectado
            </div>
            <div className="text-sm flex items-center gap-2">
              <Smartphone className="h-4 w-4 text-muted-foreground" />+{instance.displayPhoneNumber}
            </div>
            <div className="text-xs text-muted-foreground">
              Phone ID: <code className="text-foreground">{instance.phoneNumberId}</code>
            </div>
          </div>
        )}

        {/* Erro */}
        {instance.status === 'error' && instance.lastError && (
          <div className="rounded-lg border border-rose-500/30 bg-rose-500/5 p-3 text-sm text-rose-300">
            <div className="font-medium mb-1 flex items-center gap-1.5">
              <AlertCircle className="h-4 w-4" />
              Erro
            </div>
            <div className="text-xs">{instance.lastError}</div>
          </div>
        )}

        {/* Setup info (URL/verify token) */}
        {setupInfo && (
          <div className="rounded-lg border border-blue-500/30 bg-blue-500/5 p-4 space-y-3">
            <div className="text-blue-300 text-sm font-medium flex items-center gap-2">
              <ShieldCheck className="h-4 w-4" />
              Configuração no Meta App Manager
            </div>
            <div className="space-y-1.5 text-xs">
              <div>
                <div className="text-muted-foreground mb-0.5">Webhook URL</div>
                <div className="flex items-center gap-2">
                  <code className="text-foreground bg-muted px-2 py-1 rounded text-[11px] flex-1 truncate">
                    {setupInfo.webhookUrl}
                  </code>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => copyToClipboard(setupInfo.webhookUrl)}
                    className="px-2"
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
              <div>
                <div className="text-muted-foreground mb-0.5">Verify Token</div>
                <div className="flex items-center gap-2">
                  <code className="text-foreground bg-muted px-2 py-1 rounded text-[11px] flex-1 truncate">
                    {setupInfo.verifyToken ?? '(salve as credenciais primeiro)'}
                  </code>
                  {setupInfo.verifyToken && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => copyToClipboard(setupInfo.verifyToken!)}
                      className="px-2"
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground mb-0.5">Subscribe fields</div>
                <div className="flex gap-1.5 flex-wrap">
                  {setupInfo.subscribeFields.map((f) => (
                    <code key={f} className="text-foreground bg-muted px-2 py-0.5 rounded text-[10px]">
                      {f}
                    </code>
                  ))}
                </div>
              </div>
              {setupInfo.warningIfLocalhost && (
                <div className="mt-2 text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded px-2 py-1.5 text-[11px]">
                  ⚠ {setupInfo.warningIfLocalhost}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Form de credenciais */}
        {(showCredsForm || instance.status === 'disconnected' || instance.status === 'error') && (
          <div className="rounded-lg border border-border p-4 space-y-3">
            <div className="text-sm font-medium">Credenciais Meta</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <label className="text-[11px] text-muted-foreground">Phone Number ID</label>
                <input
                  type="text"
                  value={creds.phone_number_id}
                  onChange={(e) => setCreds({ ...creds, phone_number_id: e.target.value })}
                  className="w-full rounded-md bg-muted border border-border px-2 py-1.5 text-sm"
                />
              </div>
              <div>
                <label className="text-[11px] text-muted-foreground">Business Account ID</label>
                <input
                  type="text"
                  value={creds.business_account_id}
                  onChange={(e) => setCreds({ ...creds, business_account_id: e.target.value })}
                  className="w-full rounded-md bg-muted border border-border px-2 py-1.5 text-sm"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="text-[11px] text-muted-foreground">Access Token (System User permanente)</label>
                <input
                  type="password"
                  value={creds.access_token}
                  onChange={(e) => setCreds({ ...creds, access_token: e.target.value })}
                  placeholder="EAAxxxxxxxxx..."
                  className="w-full rounded-md bg-muted border border-border px-2 py-1.5 text-sm"
                />
              </div>
              <div>
                <label className="text-[11px] text-muted-foreground">App Secret</label>
                <input
                  type="password"
                  value={creds.app_secret}
                  onChange={(e) => setCreds({ ...creds, app_secret: e.target.value })}
                  className="w-full rounded-md bg-muted border border-border px-2 py-1.5 text-sm"
                />
              </div>
              <div>
                <label className="text-[11px] text-muted-foreground">Verify Token (defina você)</label>
                <input
                  type="text"
                  value={creds.verify_token}
                  onChange={(e) => setCreds({ ...creds, verify_token: e.target.value })}
                  placeholder="nextboot-wa-suporte-verify"
                  className="w-full rounded-md bg-muted border border-border px-2 py-1.5 text-sm"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={() => saveCredsMut.mutate()}
                disabled={
                  saveCredsMut.isPending ||
                  !creds.phone_number_id ||
                  !creds.business_account_id ||
                  !creds.access_token ||
                  !creds.app_secret ||
                  !creds.verify_token
                }
                className="flex items-center gap-1.5"
              >
                {saveCredsMut.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Salvar e validar
              </Button>
              {saveCredsMut.isError && (
                <span className="text-xs text-rose-400">
                  ✗ {(saveCredsMut.error as Error).message}
                </span>
              )}
              {saveCredsMut.isSuccess && saveCredsMut.data?.status === 'connected' && (
                <span className="text-xs text-emerald-400">✓ Conectou na Meta</span>
              )}
            </div>
          </div>
        )}

        {/* Botões */}
        <div className="flex flex-wrap gap-2">
          {instance.status === 'connected' && !showCredsForm && (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={() => recheckMut.mutate()}
                disabled={recheckMut.isPending}
                className="flex items-center gap-1.5"
              >
                {recheckMut.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <RefreshCw className="h-3.5 w-3.5" />
                )}
                Rechecar conexão
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => syncTemplatesMut.mutate()}
                disabled={syncTemplatesMut.isPending}
                className="flex items-center gap-1.5"
              >
                {syncTemplatesMut.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <FileText className="h-3.5 w-3.5" />
                )}
                Sincronizar templates
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setShowCredsForm(true)}
                className="flex items-center gap-1.5"
              >
                Atualizar credenciais
              </Button>
            </>
          )}
        </div>

        {/* Templates */}
        {instance.status === 'connected' && templates.length > 0 && (
          <div className="border-t border-border pt-4">
            <div className="text-xs font-medium text-muted-foreground mb-2">
              Templates HSM ({approvedTemplates.length} aprovados de {templates.length})
            </div>
            <div className="flex flex-wrap gap-1.5">
              {templates.map((t) => (
                <TemplateBadge key={t.id} template={t} />
              ))}
            </div>
          </div>
        )}

        {/* Teste de envio */}
        {instance.status === 'connected' && (
          <div className="border-t border-border pt-4 space-y-2">
            <div className="text-xs font-medium text-muted-foreground">Teste rápido de envio</div>
            <div className="flex gap-2 text-xs">
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="radio"
                  checked={testMode === 'template'}
                  onChange={() => setTestMode('template')}
                />
                Template (sempre OK)
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="radio"
                  checked={testMode === 'text'}
                  onChange={() => setTestMode('text')}
                />
                Texto livre (só dentro da janela 24h)
              </label>
            </div>
            <div className="flex flex-wrap gap-2">
              <input
                type="tel"
                placeholder="+5511999999999"
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                className="flex-1 min-w-[180px] rounded-md bg-muted border border-border px-3 py-1.5 text-sm"
              />
              {testMode === 'text' ? (
                <input
                  type="text"
                  placeholder="Texto da mensagem"
                  value={testText}
                  onChange={(e) => setTestText(e.target.value)}
                  className="flex-[2] min-w-[200px] rounded-md bg-muted border border-border px-3 py-1.5 text-sm"
                />
              ) : (
                <select
                  value={testTemplate}
                  onChange={(e) => setTestTemplate(e.target.value)}
                  className="flex-[2] min-w-[200px] rounded-md bg-muted border border-border px-3 py-1.5 text-sm"
                >
                  <option value="">Selecione um template aprovado…</option>
                  {approvedTemplates.map((t) => (
                    <option key={t.id} value={t.name}>
                      {t.name} ({t.language})
                    </option>
                  ))}
                </select>
              )}
              <Button
                size="sm"
                onClick={() => testSendMut.mutate()}
                disabled={
                  !testTo ||
                  testSendMut.isPending ||
                  (testMode === 'text' ? !testText : !testTemplate)
                }
                className="flex items-center gap-1.5"
              >
                {testSendMut.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                Enviar
              </Button>
            </div>
            {testResult && (
              <div className={cn('text-xs', testResult.startsWith('✓') ? 'text-emerald-400' : 'text-rose-400')}>
                {testResult}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function TemplateBadge({ template }: { template: WhatsAppTemplate }): JSX.Element {
  const color =
    template.status === 'APPROVED'
      ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
      : template.status === 'PENDING'
      ? 'bg-amber-500/15 text-amber-300 border-amber-500/30'
      : 'bg-rose-500/15 text-rose-300 border-rose-500/30';
  return (
    <Badge variant="outline" className={cn('text-[10px]', color)} title={template.previewText ?? ''}>
      {template.name} · {template.language}
    </Badge>
  );
}

// ============================================================
// Form para criar nova instância Cloud
// ============================================================
function AddCloudInstanceCard(): JSX.Element {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [purpose, setPurpose] = useState<'support' | 'sales' | 'recovery' | 'broadcast'>('support');

  const createMut = useMutation({
    mutationFn: () =>
      whatsappCloudClient.createInstance({ name, purpose, default_assignee_id: undefined }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wa-instances'] });
      setOpen(false);
      setName('');
    },
  });

  if (!open) {
    return (
      <Button
        variant="outline"
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 w-full justify-center"
      >
        <Plus className="h-4 w-4" />
        Adicionar instância WhatsApp Cloud
      </Button>
    );
  }

  return (
    <Card className="border-blue-500/30">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Cloud className="h-5 w-5 text-blue-400" />
          Nova instância Cloud API
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <label className="text-[11px] text-muted-foreground">Nome (ex: suporte_cloud)</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-md bg-muted border border-border px-2 py-1.5 text-sm"
          />
        </div>
        <div>
          <label className="text-[11px] text-muted-foreground">Propósito</label>
          <select
            value={purpose}
            onChange={(e) => setPurpose(e.target.value as 'support')}
            className="w-full rounded-md bg-muted border border-border px-2 py-1.5 text-sm"
          >
            <option value="support">Suporte</option>
            <option value="sales">Vendas</option>
            <option value="recovery">Recuperação</option>
            <option value="broadcast">Broadcast</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={() => createMut.mutate()}
            disabled={!name || createMut.isPending}
            className="flex items-center gap-1.5"
          >
            {createMut.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Criar
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
            Cancelar
          </Button>
          {createMut.isError && (
            <span className="text-xs text-rose-400">✗ {(createMut.error as Error).message}</span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================
// Página principal
// ============================================================
export function WhatsApp(): JSX.Element {
  const qc = useQueryClient();

  const instancesQuery = useQuery({
    queryKey: ['wa-instances'],
    queryFn: apiClient.whatsappInstances,
    refetchInterval: 10000,
  });

  const healthQuery = useQuery({
    queryKey: ['wa-health'],
    queryFn: apiClient.whatsappHealth,
    refetchInterval: 15000,
  });

  const instances = instancesQuery.data?.instances ?? [];
  const cloudInstances = instances.filter((i) => i.channelType === 'cloud');
  const baileysInstances = instances.filter((i) => i.channelType !== 'cloud');

  return (
    <div className="container mx-auto p-6 max-w-5xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <MessageCircle className="h-6 w-6 text-emerald-400" />
          WhatsApp
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Conecte WhatsApp ao CRM. Recomendado: <strong>Cloud API oficial Meta</strong> (produção).
          Baileys/Evolution apenas pra teste em dev.
        </p>
      </div>

      {/* Cloud API */}
      <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
        <Cloud className="h-5 w-5 text-blue-400" />
        WhatsApp Cloud API (oficial)
      </h2>
      <div className="space-y-4 mb-6">
        {cloudInstances.map((inst) => (
          <CloudCard key={inst.id} instance={inst} />
        ))}
        <AddCloudInstanceCard />
      </div>

      {/* Baileys section */}
      <h2 className="text-base font-semibold mb-3 mt-8 flex items-center gap-2 text-muted-foreground">
        <Server className="h-5 w-5" />
        Baileys / Evolution API (dev)
      </h2>

      <Card className="mb-4">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Server className="h-5 w-5 text-blue-400" />
            Gateway Evolution
          </CardTitle>
        </CardHeader>
        <CardContent>
          {healthQuery.isLoading ? (
            <div className="text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Verificando…
            </div>
          ) : healthQuery.data?.evolution.ok ? (
            <div className="flex flex-wrap items-center gap-4 text-sm">
              <Badge
                variant="outline"
                className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 flex items-center gap-1"
              >
                <CheckCircle2 className="h-3.5 w-3.5" />
                Online
              </Badge>
              <span className="text-muted-foreground">
                v{healthQuery.data.evolution.version} · {healthQuery.data.evolution.latencyMs}ms
              </span>
              <span className="text-muted-foreground flex items-center gap-1">
                <Activity className="h-3.5 w-3.5" />
                {healthQuery.data.instances.connected}/{healthQuery.data.instances.total} conectadas
              </span>
            </div>
          ) : (
            <div className="text-sm text-rose-400 flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              Offline: {healthQuery.data?.evolution.error ?? 'sem resposta'}
              <Button
                size="sm"
                variant="outline"
                onClick={() => qc.invalidateQueries({ queryKey: ['wa-health'] })}
                className="ml-2 flex items-center gap-1"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                Retentar
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="space-y-4">
        {baileysInstances.map((inst) => (
          <BaileysCard key={inst.id} instance={inst} />
        ))}
      </div>

      {/* Help */}
      <Card className="mt-6 bg-blue-500/5 border-blue-500/20">
        <CardContent className="py-4 text-xs text-muted-foreground space-y-2">
          <p className="font-medium text-blue-300 text-sm">Setup WhatsApp Cloud API (passo a passo)</p>
          <ol className="space-y-1 list-decimal list-inside">
            <li>
              Em{' '}
              <a
                href="https://business.facebook.com/wa/manage/phone-numbers/"
                target="_blank"
                rel="noopener noreferrer"
                className="underline text-blue-300 hover:text-blue-200 inline-flex items-center gap-1"
              >
                business.facebook.com <ExternalLink className="h-3 w-3" />
              </a>{' '}
              adicione um número WhatsApp Business.
            </li>
            <li>
              Em <strong>developers.facebook.com</strong> → Apps → seu App → WhatsApp →{' '}
              <strong>API Setup</strong>, copie <strong>Phone Number ID</strong> e{' '}
              <strong>WhatsApp Business Account ID</strong>.
            </li>
            <li>
              Em <strong>Business Settings → System Users</strong>, crie um System User e gere{' '}
              <strong>Access Token permanente</strong> com permissões{' '}
              <code>whatsapp_business_messaging</code> + <code>whatsapp_business_management</code>.
            </li>
            <li>
              Em <strong>App Settings → Basic</strong>, copie o <strong>App Secret</strong>.
            </li>
            <li>
              No card acima, clique em <strong>"Adicionar instância WhatsApp Cloud"</strong>, dê um
              nome, e cole todas as credenciais. Defina o Verify Token (qualquer string longa).
            </li>
            <li>
              Volte no Meta App → WhatsApp → <strong>Configuration → Webhook</strong>:
              <ul className="list-disc list-inside ml-4 mt-1">
                <li>
                  Callback URL: <code>{'<seu-domínio>'}/webhooks/whatsapp-cloud</code>
                </li>
                <li>Verify Token: o mesmo que você definiu acima</li>
                <li>
                  Subscribe: <code>messages</code> + <code>message_template_status_update</code>
                </li>
              </ul>
            </li>
            <li>Sincronize templates clicando em <strong>"Sincronizar templates"</strong>.</li>
          </ol>
          <p className="mt-3 text-amber-300/90">
            ⚠ Em dev local sem domínio público use <strong>ngrok</strong>:{' '}
            <code className="bg-muted px-1 rounded">ngrok http 3000</code> e defina{' '}
            <code className="bg-muted px-1 rounded">WA_CLOUD_PUBLIC_WEBHOOK_URL</code> no .env.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
